import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14 } from "styled-components";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { Asset, type Props as AssetProps } from "~/features/craft/asset";
import { takeFirst } from "~/features/app/utils";
import { getThemeStyle, type Mode } from "~/features/ui/get-theme-style";
import { Link } from "~/features/ui/link";
import { IconButton } from "~/features/ui/icon-button";
import { Tag } from "~/features/ui/tag";
import { hardwareSpecsFragment } from "~/features/blocks/inventory/hardware-comparison-table";
import ArrowRight from "~/public/icons/arrow-right.svg";
import { Button } from "../ui/button";
const fragment = graphql(`
  fragment ProductFeaturedCard on hardware_default_Entry {
    title
    url
    productCaption
    productImage {
      ...Asset
    }
    ...HardwareSpecs
  }
`);
export type ProductFeaturedCardViewProps = {
  asset?: AssetProps["asset"];
  mode: Mode;
  productCaption?: string | null;
  productHighlights?: string[] | null;
  title?: string | null;
  url?: string | null;
};
export function ProductFeaturedCardView({
  asset,
  mode = "light",
  productCaption,
  productHighlights,
  title,
  url
}: ProductFeaturedCardViewProps) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode
  });
  const highlights = productHighlights ?? [];
  return <_StyledLink href={url || undefined}>
      <_StyledDiv>
        <_StyledDiv2 $_css={[{
        "zIndex": "10",
        "height": "100%",
        "@media (min-width: 1024px)": {
          "position": "relative"
        }
      }, themeStyle.background]}>
          <_StyledDiv3>
            <_StyledDiv4>
              <IconButton as="div" icon={{
              component: ArrowRight
            }} />
            </_StyledDiv4>
            <Asset asset={asset} imageProps={{
            ratio: "490/400",
            sizes: "lg:25vw"
          }} />
          </_StyledDiv3>
        </_StyledDiv2>
      </_StyledDiv>

      <_StyledDiv5>
        <_StyledDiv6>
          <_StyledDiv7>
            <_StyledH>{title}</_StyledH>
            <_StyledSpan />
          </_StyledDiv7>

          {(productCaption || highlights.length > 0) && <_StyledDiv8>
              {productCaption && <_StyledP $_css2={[{
            "fontSize": "1.4rem",
            "lineHeight": "1.5",
            "letterSpacing": "0.014000000000000002rem",
            "fontWeight": "400"
          }, themeStyle.label]}>
                  {productCaption}
                </_StyledP>}
              {highlights.length > 0 && <_StyledDiv9>
                  {highlights.map((highlight, i) => <Tag key={i} variant="solid-light-grey">
                      {highlight}
                    </Tag>)}
                </_StyledDiv9>}
            </_StyledDiv8>}
        </_StyledDiv6>

        <_StyledDiv10>
          <Button as="div" icon={ArrowRight} variant="underline">
            Read more
          </Button>
        </_StyledDiv10>
      </_StyledDiv5>
    </_StyledLink>;
}
type Props = FragmentType<typeof fragment> & {
  mode: Mode;
};
export function ProductFeaturedCard(props: Props) {
  const {
    productCaption,
    title,
    productImage,
    url,
    ...rest
  } = useFragment(fragment, props);
  const {
    productVerticalFieldOfView,
    productRange
  } = useFragment(hardwareSpecsFragment, rest);
  // Fall back to the two available chip fields when product highlights are absent.
  const highlights = [productVerticalFieldOfView, productRange].filter((s): s is string => Boolean(s));
  const {
    mode = "light"
  } = props;
  const asset = takeFirst(productImage);
  return <ProductFeaturedCardView asset={asset} mode={mode as Mode} productCaption={productCaption} productHighlights={highlights} title={title} url={url} />;
}
var _StyledLink = _styled(Link).withConfig({
  componentId: "sc-sf-1nfam1g-0"
})({
  "display": "flex",
  "height": "100%",
  "flexDirection": "column",
  "alignItems": "flex-start",
  "gap": "var(--spacing-lg)",
  "@media (min-width: 1024px)": {
    "flexDirection": "row",
    "gap": "var(--spacing-md)"
  }
});
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-1nfam1g-1"
})({
  "display": "block",
  "height": "100%",
  "width": "100%"
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-1nfam1g-2"
})`${p => p.$_css}`;
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-1nfam1g-3"
})({
  "position": "relative",
  "display": "flex",
  "alignItems": "center",
  "justifyContent": "center",
  "borderRadius": "4px",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(0 7 4 / var(--tw-bg-opacity))"
});
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-1nfam1g-4"
})({
  "position": "absolute",
  "bottom": "var(--spacing-sm)",
  "right": "var(--spacing-sm)",
  "zIndex": "10"
});
var _StyledDiv5 = _styled6("div").withConfig({
  componentId: "sc-sf-1nfam1g-5"
})({
  "display": "flex",
  "height": "100%",
  "width": "100%",
  "flexDirection": "column",
  "justifyContent": "space-between"
});
var _StyledDiv6 = _styled7("div").withConfig({
  componentId: "sc-sf-1nfam1g-6"
})({
  "display": "flex",
  "width": "100%",
  "flexDirection": "column",
  "@media (min-width: 1024px)": {
    "padding": "var(--spacing-xl)"
  }
});
var _StyledDiv7 = _styled8("div").withConfig({
  componentId: "sc-sf-1nfam1g-7"
})({
  "display": "flex",
  "alignItems": "center",
  "justifyContent": "space-between"
});
var _StyledH = _styled9("h2").withConfig({
  componentId: "sc-sf-1nfam1g-8"
})({
  "fontSize": "2.8rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledSpan = _styled10("span").withConfig({
  componentId: "sc-sf-1nfam1g-9"
})({
  "display": "none",
  "height": "0.8rem",
  "width": "0.8rem",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
  "@media (min-width: 1024px)": {
    "display": "block"
  }
});
var _StyledDiv8 = _styled11("div").withConfig({
  componentId: "sc-sf-1nfam1g-10"
})({
  "marginTop": "auto",
  "display": "flex",
  "flexDirection": "column",
  "gap": "var(--spacing-sm)",
  "paddingBottom": "var(--spacing-xl)",
  "paddingTop": "var(--spacing-md)",
  "@media (min-width: 1024px)": {
    "paddingBottom": "0",
    "paddingTop": "var(--spacing-lg)"
  }
});
var _StyledP = _styled12("p").withConfig({
  componentId: "sc-sf-1nfam1g-11"
})`${p => p.$_css2}`;
var _StyledDiv9 = _styled13("div").withConfig({
  componentId: "sc-sf-1nfam1g-12"
})({
  "display": "flex",
  "flexWrap": "wrap",
  "gap": "var(--spacing-xs)"
});
var _StyledDiv10 = /*#__PURE__*/_styled14("div").withConfig({
  displayName: "product-featured-card___StyledDiv10",
  componentId: "sc-lb0l62-0"
})({
  "display": "none",
  "@media (min-width: 1024px)": {
    "display": "block",
    "paddingLeft": "var(--spacing-xl)",
    "paddingRight": "var(--spacing-xl)"
  }
});